/**
 * Add-Ons Configuration
 * Authoritative data model for all available add-ons with pricing, validation, and display properties
 */

import { BASE_PHOTO_PRICES, PHOTO_VIDEO_PRICES } from '@/lib/constants/packagePricing'
import {
  VIDEO_ADDON_MIN_ADVANCE_DAYS,
  VIDEO_ADDON_PRICING,
  getVideoAddOnDeliverables
} from '@/shared/videoAddOnPricing'

export const ADD_ONS_CONFIG = [
  {
    id: 'raw-footage',
    title: 'Raw Footage',
    basePrice: 395,
    originalPrice: null,
    discountPercent: 0,
    popularity: 81,
    category: 'photos',
    description: 'Receive all unedited photos from your event. Every single shot captured, delivered digitally for you to keep.',
    features: [
      'All unedited photos captured',
      'Complete day coverage',
      'Raw, unprocessed files',
      'Digital download included',
      'Professional quality images'
    ],
    extraInfo: {
      hasLink: true,
      linkText: 'View article and download instructions',
      linkAction: 'modal'
    },
    validation: {
      isAlwaysAvailable: true
    },
    badges: {
      discount: null,
      popularity: '81% of couples choose this'
    }
  },
  {
    id: 'sneak-peek-photos',
    title: 'Sneak Peek Photos',
    basePrice: 195,
    originalPrice: null,
    discountPercent: 0,
    popularity: null,
    category: 'photos',
    description: 'Get your first look at your wedding day! Receive 10 professionally edited highlight photos delivered within 10 days from the date of purchase.',
    features: [
      '10 professionally edited photos',
      'Hand-selected highlights',
      '10-day delivery from purchase',
      'High-resolution digital files',
      'Perfect for immediate sharing',
      'Social media ready'
    ],
    validation: {
      isAlwaysAvailable: true
    },
    badges: {
      discount: null,
      popularity: null,
      deliveryBadge: '10-day delivery'
    }
  },
  {
    id: 'getting-ready-photos',
    title: 'Getting Ready Photos',
    basePrice: 395,
    originalPrice: null,
    discountPercent: 0,
    popularity: null,
    category: 'photos',
    hidden: true, // Temporarily hidden from client portal
    description: 'Capture the excitement and emotions before the ceremony! Professional coverage of bride and groom preparations.',
    features: [
      '1 hour of additional coverage',
      'Bride/groom getting ready moments',
      'Hair and makeup documentation',
      'Detail shots of dress, shoes, rings',
      'Emotional family moments',
      'Professional editing included'
    ],
    validation: {
      isAlwaysAvailable: true
    },
    badges: {
      discount: null,
      popularity: null
    }
  },
  {
    id: 'liability-insurance',
    title: 'Liability Insurance',
    basePrice: 395,
    originalPrice: null,
    discountPercent: 0,
    popularity: 94,
    category: 'protection',
    description: 'Professional liability coverage protecting you from accidents or damage during your event.',
    features: [
      'Complete liability protection',
      'Equipment damage coverage',
      'Professional team insurance',
      'Venue requirement compliance',
      'Peace of mind guarantee'
    ],
    validation: {
      isAlwaysAvailable: true
    },
    badges: {
      discount: null,
      popularity: '94% of couples choose this',
      recommended: true
    }
  },
  {
    id: 'rush-delivery',
    title: 'Rush Delivery',
    basePrice: 650,
    originalPrice: null,
    discountPercent: 0,
    popularity: null,
    category: 'delivery',
    description: 'Fast-track your photos and videos with priority editing. Receive your final gallery in a fraction of the standard turnaround time.',
    features: [
      'Priority editing queue',
      'Expedited delivery',
      'First-in-line status',
      'Perfect for upcoming events',
      'Available before or within 48 hours after event'
    ],
    validation: {
      isTimeDependent: true,
      timeLimitHours: 48,
      requiresEventDate: true,
      validationMessage: 'Available before event or within 48 hours after',
      isPostEventOnly: false,
      allowPreEvent: true
    },
    badges: {
      discount: null,
      popularity: null
    }
  },
  {
    id: 'engagement-session',
    title: 'Engagement, Bridal, or Anniversary Session',
    basePrice: 750,
    originalPrice: 900,
    discountPercent: 17,
    popularity: null,
    category: 'sessions',
    description: 'Professional photo session perfect for engagement, bridal, or anniversary celebrations. Capture beautiful moments with expert photography.',
    features: [
      '2-3 hour session',
      'Professional photographer',
      '100+ edited photos',
      'Digital download included',
      'Social media ready images',
      '$199/mo payment plan'
    ],
    validation: {
      hasWarning: true,
      warningThresholdDays: 31,
      warningMessage: 'Sessions within 31 days require advance contact'
    },
    badges: {
      discount: 'Usually $900 — 17% off',
      popularity: null
    }
  },
  {
    id: 'second-photographer',
    title: 'Second Photographer',
    basePrice: 100, // $100/hour base display
    originalPrice: null,
    discountPercent: 0,
    popularity: 36,
    category: 'team',
    description: 'Add a second photographer for comprehensive dual-angle coverage. Hours automatically match your package duration.',
    features: [
      'Additional photographer',
      'Matches your package hours exactly',
      'Dual angle coverage',
      'Professional coordination'
    ],
    pricing: {
      isDynamic: true,
      hourlyRate: 100,
      minimumHours: 3,
      matchesPackageHours: true
    },
    eligibility: {
      packageTypes: ['photo', 'photoOnly', 'photoVideo'],
      excludePackageTypes: ['videoOnly'],
      message: 'Available for photo or photo+video packages only'
    },
    validation: {
      requiresMinimumHours: true,
      minimumHours: 3,
      errorMessage: 'Requires events longer than 3 hours'
    },
    badges: {
      discount: null,
      popularity: '36% of couples choose this',
      priceNote: '$100/hour'
    }
  },
  {
    id: 'second-videographer',
    title: 'Second Videographer',
    basePrice: 135, // $135/hour base display
    originalPrice: null,
    discountPercent: 0,
    popularity: null,
    category: 'team',
    description: 'Add a second videographer for comprehensive dual-angle coverage. Hours automatically match your package duration.',
    features: [
      'Additional videographer',
      'Matches your package hours exactly',
      'Dual angle coverage',
      'Professional coordination'
    ],
    pricing: {
      isDynamic: true,
      hourlyRate: 135,
      minimumHours: 3,
      matchesPackageHours: true
    },
    eligibility: {
      packageTypes: ['videoOnly', 'photoVideo'],
      excludePackageTypes: ['photo', 'photoOnly'],
      message: 'Available for video or photo+video packages only'
    },
    validation: {
      requiresMinimumHours: true,
      minimumHours: 3,
      errorMessage: 'Requires events longer than 3 hours'
    },
    badges: {
      discount: null,
      popularity: null,
      priceNote: '$135/hour'
    }
  },
  {
    id: 'date-change-flexibility',
    title: 'One-Time Date Change',
    basePrice: 195, // Traci, 15 Sep 2026: up from $50; matches the catalog row
    originalPrice: null,
    discountPercent: 0,
    popularity: 72,
    category: 'flexibility',
    description: 'Protect yourself with one emergency date change option. Peace of mind for unexpected schedule changes.',
    features: [
      'One date reschedule included',
      'No additional fees when used',
      'Valid for emergencies',
      'Peace of mind protection',
      'Must purchase at booking'
    ],
    validation: {
      isAlwaysAvailable: true,
      isBookingOnly: true
    },
    badges: {
      discount: null,
      popularity: '72% of couples choose this',
      priceWarning: 'Becomes $495 after booking'
    }
  },
  {
    id: 'video-addon',
    title: 'Video Add-On',
    basePrice: VIDEO_ADDON_PRICING[1],
    originalPrice: null,
    discountPercent: 0,
    popularity: null,
    category: 'media',
    description: 'Add partial wedding video coverage to a photo-only booking. Choose ceremony-only coverage or add more hours for a highlight film.',
    features: [
      'Available for photo-only bookings',
      'Choose 1 to 8 hours of video coverage',
      getVideoAddOnDeliverables(1),
      getVideoAddOnDeliverables(2),
      'Must be booked at least 15 days before your event'
    ],
    pricing: {
      isDynamic: true,
      priceByHours: VIDEO_ADDON_PRICING,
      minHours: 1,
      maxHours: 8,
      selectedHours: 1
    },
    eligibility: {
      packageTypes: ['photo', 'photoOnly'],
      excludePackageTypes: ['videoOnly', 'photoVideo'],
      message: 'Available for photo-only bookings only'
    },
    validation: {
      requiresPhotoOnlyPackage: true,
      requiresMinimumAdvanceDays: true,
      minimumAdvanceDays: VIDEO_ADDON_MIN_ADVANCE_DAYS,
      errorMessage: 'Video add-ons cannot be added after the event has passed'
    },
    badges: {
      discount: null,
      popularity: null,
      priceNote: 'Starts at'
    }
  },
  {
    id: 'video-coverage',
    title: 'Video Coverage',
    basePrice: 700, // Base price for display, actual price is calculated dynamically
    originalPrice: null,
    discountPercent: 0,
    popularity: null,
    category: 'media',
    description: 'Professional video coverage for your wedding. Duration automatically matches your photo package for seamless coverage.',
    features: [
      'Professional video cinematography',
      'Duration matches photo package',
      'Cinematic highlight reel',
      'Full ceremony footage',
      'Professional editing included',
      'Digital download delivery'
    ],
    pricing: {
      isDynamic: true,
      // Calculated from authoritative pricing tables (Photo+Video minus Photo-Only)
      priceByHours: Object.keys(BASE_PHOTO_PRICES).reduce((acc, hours) => {
        const h = Number(hours)
        acc[h] = PHOTO_VIDEO_PRICES[h] - BASE_PHOTO_PRICES[h]
        return acc
      }, {})
    },
    validation: {
      requiresPhotoPackage: true,
      errorMessage: 'Video coverage requires a photo package to be selected',
      durationSynced: true,
      durationMessage: 'Video duration automatically matches your photo package duration'
    },
    badges: {
      discount: null,
      popularity: null
    }
  }
]

/**
 * Get add-on configuration by ID
 * @param {string} id - Add-on ID
 * @returns {Object|null} Add-on configuration or null if not found
 */
export const getAddOnById = (id) => {
  return ADD_ONS_CONFIG.find(addon => addon.id === id) || null
}

/**
 * Get all add-ons by category
 * @param {string} category - Category filter
 * @returns {Array} Array of add-ons in the category
 */
export const getAddOnsByCategory = (category) => {
  return ADD_ONS_CONFIG.filter(addon => addon.category === category)
}

/**
 * Get add-ons that have popularity badges
 * @returns {Array} Array of add-ons with popularity data
 */
export const getPopularAddOns = () => {
  return ADD_ONS_CONFIG.filter(addon => addon.popularity !== null)
}

/**
 * Get add-ons that have discount badges
 * @returns {Array} Array of add-ons with discounts
 */
export const getDiscountedAddOns = () => {
  return ADD_ONS_CONFIG.filter(addon => addon.originalPrice && addon.originalPrice > addon.basePrice)
}

/**
 * Get add-ons that require validation
 * @returns {Array} Array of add-ons with validation requirements
 */
export const getValidationRequiredAddOns = () => {
  return ADD_ONS_CONFIG.filter(addon =>
    addon.validation?.isTimeDependent ||
    addon.validation?.requiresMinimumHours ||
    addon.validation?.hasWarning
  )
}

/**
 * Get formatted display data for an add-on
 * @param {string} id - Add-on ID
 * @param {Object} context - Booking context for dynamic pricing
 * @returns {Object} Formatted add-on data for display
 */
export const getFormattedAddOn = (id, context = {}) => {
  // Normalize legacy 'second-shooter' ID to 'second-photographer'
  const normalizedId = id === 'second-shooter' ? 'second-photographer' : id
  const addon = getAddOnById(normalizedId)
  if (!addon) return null

  let displayPrice = addon.basePrice
  let priceCalculation = null

  // Handle dynamic pricing for second photographer ($100/hour)
  if (addon.pricing?.isDynamic && normalizedId === 'second-photographer') {
    const { hoursBooked } = context
    if (hoursBooked && hoursBooked > addon.pricing.minimumHours) {
      displayPrice = hoursBooked * addon.pricing.hourlyRate
      priceCalculation = `${hoursBooked} hours × $${addon.pricing.hourlyRate}/hour = $${displayPrice}`
    } else {
      // Show hourly rate when hours not yet determined or too few hours
      displayPrice = addon.pricing.hourlyRate
      priceCalculation = null
    }
  }

  // Handle dynamic pricing for second videographer ($135/hour)
  if (addon.pricing?.isDynamic && normalizedId === 'second-videographer') {
    const { hoursBooked } = context
    if (hoursBooked && hoursBooked > addon.pricing.minimumHours) {
      displayPrice = hoursBooked * addon.pricing.hourlyRate
      priceCalculation = `${hoursBooked} hours × $${addon.pricing.hourlyRate}/hour = $${displayPrice}`
    } else {
      // Show hourly rate when hours not yet determined or too few hours
      displayPrice = addon.pricing.hourlyRate
      priceCalculation = null
    }
  }

  // Handle dynamic pricing for standalone video add-on
  if (addon.pricing?.isDynamic && normalizedId === 'video-addon') {
    const selectedHours = Number(context.videoAddOnHours || addon.pricing.selectedHours || 1)
    const calculatedPrice = addon.pricing.priceByHours[selectedHours]
    displayPrice = calculatedPrice !== undefined ? calculatedPrice : addon.basePrice
    priceCalculation = `${selectedHours} hour${selectedHours !== 1 ? 's' : ''} video coverage`
  }

  // Handle dynamic pricing for video coverage
  if (addon.pricing?.isDynamic && normalizedId === 'video-coverage') {
    const { hoursBooked } = context

    if (hoursBooked && hoursBooked > 0) {
      // Round to nearest whole hour for pricing lookup
      const roundedHours = Math.round(hoursBooked)

      // Get price from lookup table or use base price as fallback
      const calculatedPrice = addon.pricing.priceByHours[roundedHours]
      displayPrice = calculatedPrice !== undefined ? calculatedPrice : addon.basePrice
      priceCalculation = `${roundedHours} hour${roundedHours !== 1 ? 's' : ''} video coverage`
    } else {
      // If no hours booked, use base price as default
      displayPrice = addon.basePrice
      priceCalculation = null
    }
  }

  return {
    ...addon,
    displayPrice,
    priceCalculation,
    formattedPrice: `$${displayPrice}`,
    hasDiscount: addon.originalPrice && addon.originalPrice > addon.basePrice,
    discountDisplay: addon.badges?.discount || null,
    popularityDisplay: addon.badges?.popularity || null
  }
}
